import type { ReactiveFormSubmitFacadeConfiguration } from '../facade/reactive-form-submit.facade';

export interface ReactiveFormSubmitHostBridgeGoBackContext {
  readonly emitGoBack: () => void;
  readonly markForCheck?: () => void;
}

export interface ReactiveFormSubmitHostBridgeAfterSendContext<TResponse> {
  readonly emitAfterSend: (response: TResponse) => void;
  readonly markForCheck?: () => void;
}

export interface ReactiveFormSubmitHostBridgeConfiguration<TResponse>
  extends Omit<ReactiveFormSubmitFacadeConfiguration<TResponse>, 'emitGoBack' | 'emitAfterSend'> {
  readonly emitGoBack: () => void;
  readonly emitAfterSend: (response: TResponse) => void;
  readonly markForCheck?: () => void;
}

/**
 * Bridges submit facade callbacks with host-owned UI events.
 *
 * The bridge intentionally does not import Router, application request handlers or dashboard services.
 * It only keeps callback sequencing and change-detection notification consistent for host components.
 */
export class ReactiveFormSubmitHostBridge<TResponse> {
  public createFacadeConfiguration(
    configuration: ReactiveFormSubmitHostBridgeConfiguration<TResponse>,
  ): ReactiveFormSubmitFacadeConfiguration<TResponse> {
    const { emitGoBack, emitAfterSend, markForCheck, ...facadeConfiguration } = configuration;

    const goBackContext = this.createGoBackContext(emitGoBack, markForCheck);
    const afterSendContext = this.createAfterSendContext(emitAfterSend, markForCheck);

    return {
      ...facadeConfiguration,
      emitGoBack: () => this.emitGoBack(goBackContext),
      emitAfterSend: (response) => this.emitAfterSend(afterSendContext, response),
    };
  }

  public emitGoBack(context: ReactiveFormSubmitHostBridgeGoBackContext): void {
    context.emitGoBack();
    context.markForCheck?.();
  }

  public emitAfterSend(
    context: ReactiveFormSubmitHostBridgeAfterSendContext<TResponse>,
    response: TResponse,
  ): void {
    context.emitAfterSend(response);
    context.markForCheck?.();
  }

  private createGoBackContext(
    emitGoBack: () => void,
    markForCheck: (() => void) | undefined,
  ): ReactiveFormSubmitHostBridgeGoBackContext {
    if (markForCheck) {
      return { emitGoBack, markForCheck };
    }

    return { emitGoBack };
  }

  private createAfterSendContext(
    emitAfterSend: (response: TResponse) => void,
    markForCheck: (() => void) | undefined,
  ): ReactiveFormSubmitHostBridgeAfterSendContext<TResponse> {
    if (markForCheck) {
      return { emitAfterSend, markForCheck };
    }

    return { emitAfterSend };
  }
}
